import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getRanking, getUserProfile, RankingEntry, UserProfile } from '../../lib/admin';
import { RankingCard } from '../../components/ui/ranking-card';
import { BadgeDisplay } from '../../components/ui/badge-display';
import { LevelProgress } from '../../components/ui/level-progress';
import { Button } from '../../components/ui/button';
import { IconButton } from '../../components/ui/icon-button';
import { Skeleton } from '../../components/ui/skeleton';
import { motion } from 'motion/react';
import { ThemeToggle } from '../../components/ui/theme-toggle';

export default function Ranking() {
  const [ranking, setRanking] = useState<RankingEntry[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rankingData, profileData] = await Promise.all([
        getRanking(100),
        user?.id ? getUserProfile(user.id) : Promise.resolve(null)
      ]);
      setRanking(rankingData);
      setUserProfile(profileData);
    } catch (error) {
      console.error('Erro ao carregar dados do ranking:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentUserEntry = user?.id 
    ? ranking.find(entry => entry.user_id === user.id)
    : null;
  
  const currentUserPosition = currentUserEntry?.ranking_position || null;

  // Se houver 3 ou menos usuários, mostrar todos no top 3
  // Se houver mais, mostrar top 3 separado e o restante
  const top3 = ranking.slice(0, 3);
  const restOfRanking = ranking.length > 3 ? ranking.slice(3) : [];

  const abbreviateEmail = (email: string): string => {
    if (!email) return 'Usuário';
    const [localPart] = email.split('@');
    if (localPart.length <= 15) {
      return email;
    }
    return `${localPart.substring(0, 15)}...`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:via-[#2d1b3d] dark:to-[#1a0f2b] transition-colors duration-300">
        <header className="bg-white dark:bg-gradient-to-r dark:from-[#1a0f2b] dark:to-[#0d0d0d] shadow-lg border-b border-gray-200 dark:border-[#8a2be2]/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Skeleton variant="text" width="200px" height="24px" />
              <Skeleton variant="rectangular" width="100px" height="40px" className="rounded-lg" />
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton variant="rectangular" height="400px" className="rounded-xl mb-8" />
          <Skeleton variant="rectangular" height="200px" className="rounded-xl mb-8" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:via-[#2d1b3d] dark:to-[#1a0f2b] transition-colors duration-300">
      {/* Header */}
      <header className="bg-white dark:bg-gradient-to-r dark:from-[#1a0f2b] dark:to-[#0d0d0d] shadow-lg border-b border-gray-200 dark:border-[#8a2be2]/30">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 w-full">
            {/* Lado Esquerdo - Título */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <IconButton
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                size="md"
                className="flex-shrink-0"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </IconButton>
              <div className="overflow-hidden">
                <h1 className="text-base sm:text-xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu truncate">Ranking de Contribuidores</h1>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-[#c780ff] font-montserrat truncate">Top criadores de códigos</p>
              </div>
            </div>
            {/* Lado Direito - Ações */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <ThemeToggle />
              <Button
                onClick={() => navigate('/dashboard')}
                type="secondary"
                size="small"
                className="hidden sm:flex"
                prefix={<i className="ri-arrow-left-line"></i>}
              >
                Voltar
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Seção do Usuário Atual */}
        {userProfile && (
          <div className="mb-8">
            <LevelProgress
              currentLevel={userProfile.level}
              currentPoints={userProfile.points}
              pointsForNextLevel={userProfile.level * 100}
              userName={abbreviateEmail(userProfile.email)}
            />
          </div>
        )}

        {/* Top 3 Pódio */}
        {top3.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-[#e0e1dd] mb-6 font-ubuntu flex items-center space-x-2">
              <i className="ri-trophy-line text-[#6a0dad] dark:text-[#c780ff]"></i>
              <span>Top 3</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {top3.map((entry, index) => (
                <motion.div
                  key={entry.user_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className={index === 1 ? 'md:order-first' : ''}
                >
                  <div className={`
                    relative rounded-xl border-2 p-6
                    ${index === 0 ? 'md:mt-0 border-yellow-400 bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20' : ''}
                    ${index === 1 ? 'md:mt-8 border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/20 dark:to-gray-700/20' : ''}
                    ${index === 2 ? 'md:mt-12 border-amber-500 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20' : ''}
                  `}>
                    {/* Medalha */}
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className={`
                        w-12 h-12 rounded-full flex items-center justify-center text-3xl
                        ${index === 0 ? 'bg-yellow-400 text-yellow-900' : ''}
                        ${index === 1 ? 'bg-gray-300 text-gray-700' : ''}
                        ${index === 2 ? 'bg-amber-500 text-amber-900' : ''}
                      `}>
                        <i className={`
                          ${index === 0 ? 'ri-medal-fill' : ''}
                          ${index === 1 ? 'ri-medal-fill' : ''}
                          ${index === 2 ? 'ri-medal-fill' : ''}
                        `}></i>
                      </div>
                    </div>

                    <div className="text-center mt-4">
                      <div className="h-16 w-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-[#6a0dad] to-[#8a2be2] flex items-center justify-center text-white text-xl">
                        <i className="ri-user-line"></i>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu mb-1">
                        {abbreviateEmail(entry.email)}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-[#c780ff] font-montserrat mb-4">
                        Nível {entry.level} • {entry.points.toLocaleString('pt-BR')} pontos
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-2xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
                            {entry.cards_created_count.toLocaleString('pt-BR')}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-[#c780ff] font-montserrat">Cards</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
                            {entry.codes_copied_count.toLocaleString('pt-BR')}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-[#c780ff] font-montserrat">Copiados</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Badges do Usuário */}
        {userProfile && userProfile.badges.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-[#e0e1dd] mb-6 font-ubuntu flex items-center space-x-2">
              <i className="ri-award-line text-[#6a0dad] dark:text-[#c780ff]"></i>
              <span>Seus Badges</span>
            </h2>
            <BadgeDisplay badges={userProfile.badges} />
          </div>
        )}

        {/* Ranking Completo - só mostra se houver mais de 3 usuários */}
        {ranking.length > 3 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu flex items-center space-x-2">
                <i className="ri-bar-chart-line text-[#6a0dad] dark:text-[#c780ff]"></i>
                <span>Ranking Completo</span>
              </h2>
              {currentUserPosition && (
                <div className="px-4 py-2 rounded-lg bg-[#415a77]/10 dark:bg-[#778da9]/20 border border-[#415a77]/30 dark:border-[#778da9]/30">
                  <span className="text-sm font-medium text-[#6a0dad] dark:text-[#c780ff] font-montserrat">
                    Sua posição: <span className="font-bold">{currentUserPosition}º</span>
                  </span>
                </div>
              )}
            </div>

            {restOfRanking.length > 0 ? (
              <div className="space-y-3">
                {restOfRanking.map((entry) => (
                  <RankingCard
                    key={entry.user_id}
                    entry={entry}
                    isCurrentUser={entry.user_id === user?.id}
                  />
                ))}
              </div>
            ) : null}
          </div>
        )}

        {/* Mostrar posição do usuário se não estiver no top 3 */}
        {currentUserPosition && currentUserPosition > 3 && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-[#e0e1dd] mb-6 font-ubuntu flex items-center space-x-2">
              <i className="ri-user-line text-[#6a0dad] dark:text-[#c780ff]"></i>
              <span>Sua Posição</span>
            </h2>
            {currentUserEntry && (
              <RankingCard
                entry={currentUserEntry}
                isCurrentUser={true}
              />
            )}
          </div>
        )}
      </main>
    </div>
  );
}
